import { Box, Flex, Heading, Image, Spinner, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { RevelioWatermark } from '../../../../assets';
import { DashMetaRateLimitError } from '../../api/dash-meta-rate-limit-error';
import { getSharedPlot } from '../../api/shared-plots-api';
import { StaticChartRenderer } from './StaticChartRenderer';
import './shared-report.css';

export const SharedPlotViewer = () => {
  const { token } = useParams<{ token: string }>();

  const {
    data: snapshot,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['shared-plot', token],
    queryFn: () => getSharedPlot(token ?? ''),
    enabled: !!token,
    // Don't retry rate-limit errors — surface them immediately.
    retry: (failureCount, err) =>
      !(err instanceof DashMetaRateLimitError) && failureCount < 1,
    staleTime: Infinity,
  });

  if (isLoading) {
    return (
      <Flex height="100vh" alignItems="center" justifyContent="center">
        <Flex direction="column" alignItems="center" gap={4}>
          <Spinner size="xl" color="blue.500" />
          <Text color="gray.600">Loading shared plot...</Text>
        </Flex>
      </Flex>
    );
  }

  if (error instanceof DashMetaRateLimitError) {
    return (
      <Flex height="100vh" alignItems="center" justifyContent="center">
        <Flex direction="column" alignItems="center" gap={4}>
          <Heading size="md" color="gray.700">
            Too many requests
          </Heading>
          <Text color="gray.500">
            This shared plot is being viewed a lot right now. Please refresh in
            a minute.
          </Text>
        </Flex>
      </Flex>
    );
  }

  if (error || !snapshot?.component) {
    return (
      <Flex height="100vh" alignItems="center" justifyContent="center">
        <Flex direction="column" alignItems="center" gap={4}>
          <Heading size="md" color="gray.700">
            Plot not found
          </Heading>
          <Text color="gray.500">
            This shared plot link may be invalid or the plot may have been
            removed.
          </Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex
      className="shared-report-page"
      direction="column"
      width="100vw"
      height="100vh"
      padding="1rem"
    >
      {snapshot.title && (
        <Heading size="sm" noOfLines={1} paddingBottom="0.5rem">
          {snapshot.title}
        </Heading>
      )}
      <Box flex="1" minHeight={0}>
        <StaticChartRenderer
          chartDataEntry={{
            component: snapshot.component,
            props: snapshot.props,
            title: snapshot.title,
          }}
        />
      </Box>
      <Flex justifyContent="flex-end" paddingTop="0.5rem">
        <Image
          src={RevelioWatermark}
          alt="Revelio Labs"
          height="14px"
          pointerEvents="none"
        />
      </Flex>
    </Flex>
  );
};
